import React, {
  useEffect,
  useState,
  useCallback,
  createRef,
  Fragment,
} from "react";
import { useDispatch } from "react-redux";
import dayjs from "dayjs";
import localeData from "dayjs/plugin/localeData";
import objectSupport from "dayjs/plugin/objectSupport";

import { changesSlice } from "redux/features/changes";

import IconButton from "util/iconButton";
import { FaAngleDown } from "react-icons/fa";
import MonthPicker from "react-month-picker";
import { RadioColumns, CheckboxColumns } from "util/optionColumns";
import { Message, Content } from "react-bulma-components";
import { ShowStatus } from "./statusDetails";

dayjs.extend(localeData);
dayjs.extend(objectSupport);

/*

These functions handle the gathering of the necessary effective dates of
any affiliation status changes that are being made. This is done in three
steps:

 1. The usePrepareDatesSection hook determines whether or not there should be
    any prompting for dates, based on the nature of the current status record
    being edited and whether or not there have been any affiliation status
    changes other than date. It also takes care of clearing any date-related
    changes that are no longer needed or appropriate.

    If date prompting is not appropriate, it returns null. If date prompting
    would have been appropriate except the current information is incomplete
    and should be completed in its entirety first before changes are made to
    only part of it, { defer: true} is rteturned. Otherwise, an object is
    returned without a defer entry, containing a variety of properties
    that can be used to determine how dates should be prompted for.

 2. Then, the DatesSection component will be rendered only if a full
    object (without defer) was returned by usePrepareDatesSection.
    
    This DatesSection component will initialize date changes to the default
    settings when first mounted, but will render an actual prompt only if
    the entered affiliation status information is complete enough that the
    the user can be asked for dates in an intelligible way.

    The separation between (1) and (2) ensures that, if the actual prompt
    is unmounted because the user's input is incomplete, any default or
    user-selected dates are preserved, but if the changes are cancelled
    altother and the entire DatesSection component is unmounted, then
    the user-selected dates are cleared.

 3. The actual prompt, if needed by both (1) and (2) determine it is needed,
    is rendered by DatesSectionRegularPrompt - except in the anomalous case
    of editing a status record that has an effective date in the future
    (something that should not happen for information entered normally, but
    is technically permitted by the data model). In that case a
    DatesSectionFuturePrompt is rendered, which does nothing except give the
    user a chance to reset the effective date back to today's date instead
    of being in the future.
*/

/* Two utility functions: */

// (1) We don't want to ask the user to select a specific date for the start
// of a status; a month is good enough. We record months as "YYYY-MM". But we
// convert that into a YYYY-MM-DD date to store in the updated status record:
// first of the month for a from date, last of the month for a through date.

const monthDate = (field, month) =>
  field === "from"
    ? month + "-01"
    : dayjs(month + "-01")
        .endOf("month")
        .format("YYYY-MM-DD");

// (2) Reverse this: from the changed date in the status record, get the
// corresponding selected month. We could just take a substring but this
// allows us the flexibility of differently-formatted dates in the future.
const dateMonth = (date) => dayjs(date).format("YYYY-MM");

export function usePrepareDatesSection({
  originalStatusRecord,
  wasIncomplete,
  merelyAugmented,
  changes,
  changeKeys,
}) {
  const { from, through, entered_from } = originalStatusRecord || {};
  const now = dayjs();
  const today = now.format("YYYY-MM-DD");
  const isLatest = through === null;
  const isFuture = isLatest && from && from > today;

  const actualFrom = from ?? entered_from ?? through ?? today;

  const monthFrom = dayjs(actualFrom).format("YYYY-MM");
  const showFrom =
    originalStatusRecord.showFrom || dayjs(actualFrom).format("MMMM YYYY");
  const monthAfterFrom = dayjs(actualFrom).add(1, "month").format("YYYY-MM");
  const thisMonth = now.format("YYYY-MM");
  const lastMonth = now.subtract(1, "month").format("YYYY-MM");
  const monthThrough = isLatest ? null : dayjs(through).format("YYYY-MM");
  const monthBeforeThrough = isLatest
    ? null
    : dayjs(through).subtract(1, "month").format("YYYY-MM");

  const fromSelectionRange = isLatest
    ? [monthAfterFrom, lastMonth]
    : [monthAfterFrom, monthThrough];
  const throughSelectionRange = [monthFrom, monthBeforeThrough];

  const canChooseUpcomingOrPast =
    isLatest && !isFuture && monthFrom < thisMonth;

  const selectableFrom =
    !isFuture && fromSelectionRange[0] <= fromSelectionRange[1]
      ? fromSelectionRange
      : null;
  const selectableThrough =
    !isLatest && throughSelectionRange[0] <= throughSelectionRange[1]
      ? throughSelectionRange
      : null;

  const canSelectDates =
    canChooseUpcomingOrPast || selectableFrom || selectableThrough;

  const changesCopy = { ...changes };
  delete changesCopy.from;
  delete changesCopy.through;
  const haveStatusChange = Object.keys(changesCopy).length > 0;

  const isOrWillBeNeeded = isFuture || (haveStatusChange && canSelectDates);
  const waitForCompletion = isOrWillBeNeeded && !isFuture && wasIncomplete;
  const omitBecauseMerelyAugmented =
    isOrWillBeNeeded && !isFuture && merelyAugmented;
  const isNeeded =
    isOrWillBeNeeded && !waitForCompletion && !omitBecauseMerelyAugmented;

  const abilities = {
    canChooseUpcomingOrPast,
    selectableFrom,
    selectableThrough,
  };

  const dispatch = useDispatch();

  const setDate = useCallback(
    (field, value) => {
      if (value)
        dispatch(
          changesSlice.actions.setChanged({ ...changeKeys, field, value })
        );
      else
        dispatch(changesSlice.actions.clearChanged({ ...changeKeys, field }));
    },
    [dispatch, changeKeys]
  );

  useEffect(() => {
    if (!isNeeded) {
      if (changes.from) setDate("from", undefined);
      if (changes.through) setDate("through", undefined);
    }
    if (isFuture) {
      if (changes.from && changes.from !== today) setDate("from", undefined);
      if (changes.through) setDate("through", undefined);
    }
  }, [isNeeded, isFuture, today, changes.from, changes.through, setDate]);

  return isNeeded
    ? { now, today, isLatest, isFuture, setDate, changes, abilities, showFrom }
    : waitForCompletion
    ? { defer: true }
    : omitBecauseMerelyAugmented
    ? { omit: true }
    : null;
}

function DatesSectionFuturePrompt({
  originalStatusRecord,
  changes,
  setDate,
  today,
}) {
  return (
    <div>
      This information is currently scheduled to take effect only in the future,
      starting {originalStatusRecord.enteredFrom}.
      <CheckboxColumns
        value={!!changes.from}
        onChange={(checked) => setDate("from", checked ? today : undefined)}
      ></CheckboxColumns>
    </div>
  );
}

function DatesSectionRegularPrompt({
  originalStatusRecord,
  originalStatusDetails,
  proposedStatusDetails,
  showFrom,
  whose,
  options,
  selectedOptionKey,
  whatChanged,
  showSettings,
  setDate,
}) {
  const { showThrough } = originalStatusRecord;
  const nullableShowFrom = originalStatusRecord.showFrom;
  return (
    <Message color="dark">
      <Message.Header>
        When should we apply this change in {whatChanged}?
      </Message.Header>
      <Message.Body>
        <Content>
          <p className="is-italic mb-0">
            Information currently in our database for the period{" "}
            {nullableShowFrom ? " from " + nullableShowFrom + " " : null}
            {showThrough ? " through " + showThrough : " onwards"}
            {nullableShowFrom ? null : ` (first used ${showFrom})`}:
          </p>
          <ul className="mt-1">
            <li>
              <ShowStatus
                statusDetails={originalStatusDetails}
                settings={showSettings}
              />
            </li>
          </ul>
          <p className="is-italic mb-0">Replacement information will be:</p>
          <ul className="mt-1">
            <li>
              <ShowStatus
                statusDetails={proposedStatusDetails}
                settings={showSettings}
              />
            </li>
          </ul>
          <p className="is-italic mb-0">When should the replacement happen?</p>
          <div className="indent">
            {options.map((option) => (
              <RadioColumns
                key={option.key}
                name="affiliation-status-date-select"
                value={selectedOptionKey}
                optionValue={option.key}
                onChange={option.onSelected}
              >
                {option.choiceText}
                {option.includeMonthPrompt ? (
                  <MonthPrompt {...option.includeMonthPrompt} />
                ) : null}
              </RadioColumns>
            ))}
          </div>{" "}
        </Content>
      </Message.Body>
    </Message>
  );
}

function MonthPrompt({ field, range, selected, setDate }) {
  const pickerRef = createRef(null);

  const firstDayJS = dayjs(monthDate(field, range[0]));
  const lastDayJS = dayjs(monthDate(field, range[1]));
  const selectedDayJS = dayjs(monthDate(field, selected));
  const pickerRange = {
    min: { year: firstDayJS.year(), month: 1 + firstDayJS.month() },
    max: { year: lastDayJS.year(), month: 1 + lastDayJS.month() },
  };
  return (
    <div>
      <IconButton
        icon={FaAngleDown}
        rightIcon
        size="medium"
        onClick={() => {
          if (pickerRef) pickerRef.current.show();
          setDate(monthDate(field, selected));
        }}
      >
        {selectedDayJS.format("MMMM YYYY")}
      </IconButton>
      <MonthPicker
        ref={pickerRef}
        years={pickerRange}
        value={{ year: selectedDayJS.year(), month: 1 + selectedDayJS.month() }}
        lang={dayjs.monthsShort()}
        onChange={(year, month) => {
          const yyyymm = dayjs({ year, month: month - 1 }).format("YYYY-MM");
          setDate(field, monthDate(field, yyyymm));
          pickerRef.current.dismiss();
        }}
      />
    </div>
  );
}

export function DatesSection({
  originalStatusRecord,
  originalStatusDetails,
  proposedStatusDetails,
  statusChangeSummary,
  preparation,
  whose,
}) {
  const { showThrough } = originalStatusRecord || {};

  const {
    today,
    isLatest,
    isFuture,
    setDate,
    changes,
    abilities,
    showFrom,
  } = preparation;

  const {
    canChooseUpcomingOrPast,
    selectableFrom,
    selectableThrough,
  } = abilities;
  const [initialized, setInitialized] = useState(false);
  useEffect(() => {
    if (!initialized) {
      setInitialized(true);
      if (canChooseUpcomingOrPast) setDate("from", today);
    }
  }, [initialized, setDate, today, canChooseUpcomingOrPast]);

  const options = [];
  const selectedOptionKey = changes.from
    ? canChooseUpcomingOrPast && changes.from === today
      ? "upcoming"
      : "laterFrom"
    : changes.through
    ? "earlierThrough"
    : "full";

  if (canChooseUpcomingOrPast) {
    options.push({
      key: "upcoming",
      choiceText: (
        <Fragment>
          <b>Today.</b> Use the new information for current/upcoming Fields
          registrations; keep the old information for past ones.
        </Fragment>
      ),
      onSelected: () => {
        setDate("from", today);
        setDate("through", undefined);
      },
    });
  }

  options.push({
    key: "full",
    choiceText: isLatest ? (
      <Fragment>
        <b>For the entire period.</b> The old information was wrong and should
        be corrected.
      </Fragment>
    ) : (
      <Fragment>
        <b>For the entire period.</b> The old information was wrong and should
        be corrected.
      </Fragment>
    ),
    onSelected: () => {
      setDate("from", undefined);
      setDate("through", undefined);
    },
  });

  if (selectableFrom) {
    const defaultMonth = selectableFrom[0];
    const isSelected = selectedOptionKey === "laterFrom";
    options.push({
      key: "laterFrom",
      choiceText: (
        <Fragment>
          <b>
            {isLatest ? "In the past" : "For the end of the period"}, but not as
            far back as {showFrom}
          </b>
          . The new information should start in:
        </Fragment>
      ),

      onSelected: () => {
        // Click could get fired after month change in month picker; don't
        // reset back to the original month in that case! Only set date
        // when not already selected.
        if (!isSelected) setDate("from", monthDate("from", selectableFrom[0]));
        setDate("through", undefined);
      },
      includeMonthPrompt: {
        field: "from",
        range: selectableFrom,
        selected: isSelected ? dateMonth(changes.from) : defaultMonth,
        setDate,
      },
    });
  }
  if (selectableThrough) {
    const defaultMonth = selectableThrough[1];
    const isSelected = selectedOptionKey === "earlierThrough";
    options.push({
      key: "earlierThrough",
      choiceText: (
        <Fragment>
          <b>For the beginning part of the period only.</b>
          The old information should be kept for {showThrough} but the new
          information should replace it from {showFrom} until:
        </Fragment>
      ),

      onSelected: () => {
        setDate("from", undefined);
        if (!isSelected) setDate("through", monthDate("through", defaultMonth));
      },

      includeMonthPrompt: {
        field: "through",
        range: selectableThrough,
        selected: isSelected ? dateMonth(changes.through) : defaultMonth,
        setDate,
      },
    });
  }

  const { nowIncomplete, whatChanged, showSettings } = statusChangeSummary;
  return nowIncomplete ? null : isFuture ? (
    <DatesSectionFuturePrompt
      {...{ originalStatusRecord, changes, setDate, today }}
    />
  ) : (
    <DatesSectionRegularPrompt
      {...{
        originalStatusRecord,
        originalStatusDetails,
        proposedStatusDetails,
        showFrom,
        whose,
        options,
        selectedOptionKey,
        whatChanged,
        showSettings,
        setDate,
      }}
    />
  );
}
